import type { ApiResult } from './types';
import { caseApiRequest } from './apiClient';

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const bool = (v: unknown) => (typeof v === 'boolean' ? v : undefined);

export const SUBSIDIARY_TASK_NAME_MAX_LENGTH = 100;
export const SUBSIDIARY_TASK_DESCRIPTION_MAX_LENGTH = 255;

export type SubsidiaryTaskListRow = {
  id: string;
  name: string;
  description: string;
  active: boolean;
  updatedAt: string;
};

export type SubsidiaryTaskDetail = {
  id: string;
  name: string;
  code: string;
  description: string;
  active: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
};

export type SubsidiaryTaskListParams = {
  page: number;
  pageSize: number;
  /** Filter by subsidiary task name. */
  subsidiaryTask?: string;
  /** When omitted, list includes all statuses. */
  active?: boolean;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  signal?: AbortSignal;
};

export type SubsidiaryTaskListResult = {
  items: SubsidiaryTaskListRow[];
  total: number;
  totalPages: number;
};

export type SaveSubsidiaryTaskInput = {
  name: string;
  description?: string;
  active: boolean;
};

type ApiRow = Record<string, unknown>;

function pickStr(o: ApiRow, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function unwrapRow(data: unknown): ApiRow {
  if (!data || typeof data !== 'object') return {};
  const record = data as ApiRow;
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as ApiRow;
  }
  return record;
}

function unwrapRows(data: unknown): ApiRow[] {
  if (Array.isArray(data)) return data as ApiRow[];
  if (!data || typeof data !== 'object') return [];
  const record = data as ApiRow;
  if (Array.isArray(record.content)) return record.content as ApiRow[];
  if (Array.isArray(record.data)) return record.data as ApiRow[];
  if (Array.isArray(record.items)) return record.items as ApiRow[];
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = nested as ApiRow;
    if (Array.isArray(inner.content)) return inner.content as ApiRow[];
    if (Array.isArray(inner.data)) return inner.data as ApiRow[];
  }
  return [];
}

function pageMeta(data: unknown): { total: number; totalPages: number } | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const record = data as ApiRow;
  const sources = [record];
  const nested = record.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    sources.push(nested as ApiRow);
  }

  for (const source of sources) {
    const total =
      typeof source.totalElements === 'number'
        ? source.totalElements
        : typeof source.total === 'number'
          ? source.total
          : null;
    const totalPages =
      typeof source.totalPages === 'number' && source.totalPages > 0 ? source.totalPages : null;
    if (total != null) {
      return {
        total,
        totalPages: totalPages ?? Math.max(1, Math.ceil(total / 1)),
      };
    }
  }
  return null;
}

function mapListRow(row: ApiRow): SubsidiaryTaskListRow | null {
  const id = pickStr(row, 'id', 'subsidiary_task_id', 'subsidiaryTaskId');
  const name = pickStr(row, 'subsidiary_task', 'subsidiaryTask', 'name');
  if (!id || !name) return null;

  return {
    id,
    name,
    description: pickStr(row, 'description'),
    active: bool(row.active) ?? bool(row.status) ?? true,
    updatedAt: pickStr(
      row,
      'last_updated_at',
      'lastUpdatedAt',
      'updated_at',
      'updatedAt',
    ),
  };
}

function mapDetail(row: ApiRow): SubsidiaryTaskDetail | null {
  const id = pickStr(row, 'id', 'subsidiary_task_id', 'subsidiaryTaskId');
  const name = pickStr(row, 'subsidiary_task', 'subsidiaryTask', 'name');
  if (!id || !name) return null;

  return {
    id,
    name,
    code: pickStr(row, 'code'),
    description: pickStr(row, 'description'),
    active: bool(row.active) ?? bool(row.status) ?? true,
    createdAt: pickStr(row, 'created_at', 'createdAt'),
    createdBy: pickStr(
      row,
      'created_by_name',
      'createdByName',
      'created_by',
      'createdBy',
    ),
    updatedAt: pickStr(
      row,
      'last_updated_at',
      'lastUpdatedAt',
      'updated_at',
      'updatedAt',
    ),
    updatedBy: pickStr(
      row,
      'last_updated_by_name',
      'lastUpdatedByName',
      'updated_by_name',
      'updatedByName',
      'last_updated_by',
      'lastUpdatedBy',
      'updated_by',
      'updatedBy',
    ),
  };
}

function parsePage(
  data: unknown,
  page: number,
  pageSize: number,
): SubsidiaryTaskListResult {
  const rows = unwrapRows(data)
    .map(mapListRow)
    .filter((row): row is SubsidiaryTaskListRow => row != null);

  const meta = pageMeta(data);
  if (meta) {
    const totalPages =
      meta.totalPages > 0
        ? meta.totalPages
        : Math.max(1, Math.ceil(meta.total / Math.max(1, pageSize)));
    return { items: rows, total: meta.total, totalPages };
  }

  const total = rows.length;
  const start = page * pageSize;
  return {
    items: rows.slice(start, start + pageSize),
    total,
    totalPages: Math.max(1, Math.ceil(total / Math.max(1, pageSize))),
  };
}

function buildSearchBody(params: SubsidiaryTaskListParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const subsidiaryTask = params.subsidiaryTask?.trim();
  const sortBy = params.sortBy?.trim();
  const sortDir = params.sortDir?.trim();

  if (subsidiaryTask) body.subsidiary_task = subsidiaryTask;
  if (typeof params.active === 'boolean') body.active = params.active;
  if (sortBy) body.sort_by = sortBy;
  if (sortDir) body.sort_direction = sortDir;

  return body;
}

function buildSaveBody(input: SaveSubsidiaryTaskInput): Record<string, unknown> {
  return {
    subsidiary_task: input.name.trim(),
    description: input.description?.trim() ?? '',
    active: input.active,
  };
}

/** POST /v1/case-requests/masters/subsidiary-tasks/search — CMS-MST-2 */
export async function searchSubsidiaryTasks(
  params: SubsidiaryTaskListParams,
): Promise<ApiResult<SubsidiaryTaskListResult>> {
  const page = Math.max(0, params.page);
  const pageSize = Math.max(1, params.pageSize);
  const qs = new URLSearchParams({
    page: String(page),
    size: String(pageSize),
  });

  const res = await caseApiRequest<unknown>(
    `/masters/subsidiary-tasks/search?${qs.toString()}`,
    {
      method: 'POST',
      body: buildSearchBody(params),
      signal: params.signal,
    },
  );

  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: parsePage(res.data, page, pageSize) };
}

/** GET /v1/case-requests/masters/subsidiary-tasks/{id} — CMS-MST-3 */
export async function getSubsidiaryTaskById(
  id: string,
): Promise<ApiResult<SubsidiaryTaskDetail>> {
  const subsidiaryTaskId = id.trim();
  if (!subsidiaryTaskId) return { success: false, message: 'Subsidiary task id is missing.' };

  const res = await caseApiRequest<unknown>(
    `/masters/subsidiary-tasks/${encodeURIComponent(subsidiaryTaskId)}`,
    { method: 'GET' },
  );
  if (!res.ok) return { success: false, message: res.message };

  const detail = mapDetail(unwrapRow(res.data));
  if (!detail) return { success: false, message: 'Subsidiary task details are unavailable.' };
  return { success: true, data: detail };
}

/** POST /v1/case-requests/masters/subsidiary-tasks — CMS-MST-4 */
export async function createSubsidiaryTask(
  input: SaveSubsidiaryTaskInput,
): Promise<ApiResult<void>> {
  const res = await caseApiRequest<unknown>('/masters/subsidiary-tasks', {
    method: 'POST',
    body: buildSaveBody(input),
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** PUT /v1/case-requests/masters/subsidiary-tasks/{id} — CMS-MST-5 */
export async function updateSubsidiaryTask(
  id: string,
  input: SaveSubsidiaryTaskInput,
): Promise<ApiResult<void>> {
  const subsidiaryTaskId = id.trim();
  if (!subsidiaryTaskId) return { success: false, message: 'Subsidiary task id is missing.' };

  const res = await caseApiRequest<unknown>(
    `/masters/subsidiary-tasks/${encodeURIComponent(subsidiaryTaskId)}`,
    {
      method: 'PUT',
      body: buildSaveBody(input),
    },
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}
